import React from 'react';
import { useI18n } from '../i18n/I18nProvider';
import { useApiClient, getMyLocations, deleteLocation, toggleLocationFavorite, LocationDetailsDto } from '../api/client';
import { Link } from 'react-router-dom';
import { useToast } from '../toast/ToastProvider';
import { formatDateTime } from '../utils/dateFormat';

export const UserLocationsListPage: React.FC = () => {
  const { t } = useI18n();
  const { request } = useApiClient();
  const [locations, setLocations] = React.useState<LocationDetailsDto[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [deleting, setDeleting] = React.useState<string | null>(null);
  const [toggling, setToggling] = React.useState<string | null>(null);
  const { showSuccess, showError } = useToast();

  const loadLocations = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getMyLocations(request);
      setLocations(data);
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [request]);

  React.useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  const handleToggleFavorite = async (id: string, currentValue: boolean) => {
    setToggling(id);
    try {
      await toggleLocationFavorite(request, id, !currentValue);
      // Update local state optimistically
      setLocations(prev => prev.map(loc => 
        loc.id === id ? { ...loc, isFavorite: !currentValue } : loc
      ));
    } catch (e: any) {
      showError(`${t('common.error')}: ${e.message || e}`);
    } finally {
      setToggling(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('locations.confirmDelete'))) return;
    setDeleting(id);
    try {
      await deleteLocation(request, id);
      showSuccess(t('locations.deleteSuccess'));
      await loadLocations();
    } catch (e: any) {
      showError(`${t('common.error')}: ${e.message || e}`);
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-extrabold">{t('locations.title')}</h1>
        <Link
          to="/locations/new"
          className="rounded-lg bg-primary px-4 py-2 text-sm font-extrabold text-black hover:brightness-95"
        >
          {t('locations.createNew')}
        </Link>
      </div>

      {loading && <div className="text-sm text-gray-600">{t('common.loading')}</div>}
      {error && (
        <div className="rounded bg-red-50 p-3 text-sm text-red-900 border border-red-200">
          {t('common.error')}: {error}
        </div>
      )}

      {!loading && !error && locations.length === 0 && (
        <div className="rounded bg-gray-50 p-4 text-center text-gray-600">
          {t('locations.noLocations')}
        </div>
      )}

      {!loading && !error && locations.length > 0 && (
        <div className="grid gap-4">
          {locations.map((location) => (
            <div key={location.id} className="rounded border bg-white p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <button
                      onClick={() => handleToggleFavorite(location.id, location.isFavorite)}
                      disabled={toggling === location.id}
                      className="p-1 hover:scale-110 transition-transform disabled:opacity-50"
                      title={location.isFavorite ? t('locations.removeFromFavorites') : t('locations.addToFavorites')}
                    >
                      <svg
                        className="w-5 h-5"
                        fill={location.isFavorite ? '#ef4444' : 'none'}
                        stroke={location.isFavorite ? '#ef4444' : '#d1d5db'}
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                        />
                      </svg>
                    </button>
                    <h3 className="text-lg font-semibold">
                      {location.isPrivate && location.privateName
                        ? location.privateName
                        : location.publicName}
                    </h3>
                    {location.isPrivate && (
                      <span className="chip bg-gray-100 text-xs">{t('locations.private')}</span>
                    )}
                  </div>
                  {location.isPrivate && location.privateName && (
                    <div className="text-sm text-gray-600 mb-1">
                      {t('locations.publicName')}: {location.publicName}
                    </div>
                  )}
                  <div className="text-sm text-gray-600">
                    {location.longitude.toFixed(5)}, {location.latitude.toFixed(5)}
                  </div>
                  <div className="text-xs text-gray-400 mt-2">
                    {t('common.created')}: {formatDateTime(location.createdAt)}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <Link
                    to={`/locations/${location.id}/edit`}
                    className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200"
                  >
                    {t('action.edit')}
                  </Link>
                  <button
                    onClick={() => handleDelete(location.id)}
                    disabled={deleting === location.id}
                    className="rounded-lg bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50"
                  >
                    {deleting === location.id ? t('common.deleting') : t('action.delete')}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserLocationsListPage;

